/**
 * @typedef {Object} SwipeItem
 * @property {HTMLElement} node - The swipe node element.
 * @property {HTMLElement} content - The content element within the swipe node.
 * @property {HTMLElement} prevButton - The previous button element.
 * @property {HTMLElement} nextButton - The next button element.
 */
import { debounce } from '../../utils/debounce.js';

export const Swipe = {
	_selectors: {
		node: '.c-tk-swipe',
	},

	_config: {
		scrollSpeed: 12, // Speed of scroll in pixels
	},

	/**
	 * @type {SwipeItem[]}
	 */
	_items: [],

	init() {
		document.querySelectorAll(this._selectors.node).forEach((node) => {
			this._initializeNode(node);
		});

		window.addEventListener(
			'resize',
			debounce(() => this._items.forEach((item) => this._setItemSwipable(item), 50))
		);
	},

	_initializeNode(node) {
		if (node.classList.contains('c-tk-swipe--initialized')) {
			return;
		}

		const content = node.querySelector('.c-tk-swipe__content');

		if (!content) {
			console.warn('Swipe: Missing content in node', node);
			return;
		}

		const prevButton = node.querySelector('.c-tk-swipe__button--prev');
		const nextButton = node.querySelector('.c-tk-swipe__button--next');

		if (!prevButton || !nextButton) {
			console.warn('Swipe: Missing buttons in node', node);
			return;
		}

		/**
		 * @typedef {Object} SwipeItem
		 */
		const item = {
			node,
			content,
			prevButton,
			nextButton,
		};

		this._items.push(item);

		this._setItemSwipable(item);

		content.addEventListener(
			'scroll',
			debounce(() => this._handleItemScroll(item), 50)
		);

		const width = content.clientWidth;

		prevButton.addEventListener('click', () => {
			content.scrollLeft -= width + this._config.scrollSpeed;
		});
		nextButton.addEventListener('click', () => {
			content.scrollLeft += width + this._config.scrollSpeed;
		});
	},

	_setItemSwipable(item) {
		const nodeWidth = item.content.clientWidth;
		const nodeFullWidth = item.content.scrollWidth;

		if (nodeWidth !== nodeFullWidth && item.content.scrollLeft < nodeFullWidth) {
			item.node.classList.add('c-tk-swipe--swipable-next');
		} else {
			item.node.classList.remove('c-tk-swipe--swipable-next');
		}
	},

	_handleItemScroll(item) {
		const nodeWidth = item.content.clientWidth;
		const nodeFullWidth = item.content.scrollWidth;

		if (nodeWidth === nodeFullWidth) {
			return;
		}

		if (item.content.scrollLeft < nodeFullWidth - nodeWidth) {
			item.node.classList.add('c-tk-swipe--swipable-next');
		} else {
			item.node.classList.remove('c-tk-swipe--swipable-next');
		}

		if (item.content.scrollLeft >= this._config.scrollSpeed) {
			item.node.classList.add('c-tk-swipe--swipable-prev');
		} else {
			item.node.classList.remove('c-tk-swipe--swipable-prev');
		}
	},
};

Swipe.init();
